import {Util} from '../../../util/Util';

/**
 * How much of a folder tree to open when a picker is first shown.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * `TreeSelectorComponent` is fed `{'': projectFolders.getJSON()}`, so the first
 * node in the tree is a synthetic, *nameless* wrapper standing for the root.
 * Everything an operator actually wants to pick lives underneath it, and the
 * tree shipped fully collapsed — so every Move dialog opened onto a single
 * blank row with a chevron, and picking any folder at all began with a click
 * that conveyed no information.
 *
 * The obvious fix, `treeControl.expandAll()`, is wrong in the other direction.
 * A publisher who has built out a real filing system can have well over a
 * hundred folders; the picker is a 320px-tall scroll box (see `dialogs.css`),
 * so expanding all of it replaces "one useless click" with "scroll through
 * six screens to find anything".
 *
 * ---------------------------------------------------------------------------
 * THE RULE
 * ---------------------------------------------------------------------------
 * Expansion is chosen by how many ROWS it puts on screen, not by depth:
 *
 *   1. If the whole tree fits inside `rowBudget`, expand the whole tree.
 *      Depth is irrelevant here — 15 folders four levels deep is a small
 *      tree and showing all of it is strictly more useful than hiding
 *      two thirds of it.
 *   2. Otherwise expand whole levels, deepest-first-that-fits: open level 0,
 *      then level 1, then level 2 ... stopping before the first level whose
 *      children would push the row count past `rowBudget`. A broad tree
 *      therefore opens one or two levels; a narrow-but-deep one opens more,
 *      because it can afford to.
 *   3. Level 0 is ALWAYS expanded, even if it alone blows the budget. That is
 *      the point of the exercise: the nameless root must never be something
 *      the operator has to open by hand.
 *   4. Whatever the above decided, the chain from the root down to
 *      `currentPath` is expanded as well, so the folder the operator is
 *      currently in is on screen without hunting for it.
 *
 * Deciding by rows is what makes 1 and 2 the same rule rather than two
 * competing ones: "expand everything" is just the case where the budget was
 * never reached.
 *
 * The whole thing is one breadth-first pass over the tree — O(nodes) — and it
 * runs once per dialog open, on data that is already in memory.
 *
 * Kept free of Angular imports on purpose so `tests/TreeExpansion.smoke.ts`
 * can exercise it under ts-node.
 */

/** The shape this planner needs. `FileNode` satisfies it. */
export interface ExpandableTreeNode {
  name?: string;
  path: string;
  children?: ExpandableTreeNode[];
}

export interface TreeExpansionOptions {
  /**
   * Rows the opened tree may occupy before level-by-level expansion stops.
   *
   * Not a folder count: a row is a folder that is *visible*, which is what
   * actually costs the operator scrolling. Default {@link DEFAULT_ROW_BUDGET}.
   */
  rowBudget?: number;

  /**
   * Hard ceiling on the level that auto-expansion may reach, 0-based, where
   * level 0 is `roots`. Negative or omitted means no ceiling — the budget is
   * normally the only limit worth having. Present for hosts that render the
   * tree somewhere much smaller than a dialog.
   */
  maxDepth?: number;

  /**
   * The folder the operator is in right now, if the host knows it. Its whole
   * ancestor chain is expanded regardless of what the budget decided.
   */
  currentPath?: string;
}

export interface TreeExpansionPlan<T extends ExpandableTreeNode> {
  /** Nodes to hand to `treeControl.expand(...)`, parents before children. */
  expand: T[];
  /** Every node in the tree, deduplicated. */
  totalNodes: number;
  /** Rows the tree will occupy once {@link expand} has been applied. */
  visibleRows: number;
  /** Deepest level expanded by the budget rule. -1 when the tree is empty. */
  depth: number;
  /** True when every node with children ended up expanded. */
  wholeTree: boolean;
}

/**
 * 40 visible rows.
 *
 * Above the ~10 rows the 320px picker shows without scrolling — a tree that
 * needs a little scrolling is still far easier to work with than one that
 * hides where everything is — and below the point where scanning it stops
 * being realistic. "A couple of dozen folders" lands inside it and opens
 * completely; a deliberately built-out filing system does not.
 */
export const DEFAULT_ROW_BUDGET = 40;

function hasChildren(node: ExpandableTreeNode): boolean {
  return !!(node && node.children && node.children.length > 0);
}

/**
 * `['', '/A', '/A/B']` for `/A/B`. The root is always first, because the
 * synthetic wrapper node is what holds every other folder.
 */
export function ancestorPaths(path: string): string[] {
  const normalized = Util.normalizeFolderPath(path);
  const chain: string[] = [''];
  if (normalized === '') {
    return chain;
  }
  let accumulated = '';
  for (const segment of normalized.split('/')) {
    if (segment === '') {
      continue;
    }
    accumulated += '/' + segment;
    chain.push(accumulated);
  }
  return chain;
}

/**
 * Rows on screen if exactly `expanded` are open.
 *
 * `visited` is not an optimization. A cyclic tree makes this recurse until the
 * stack blows, and it blows *here* rather than in the walk above — which is
 * how it was found. Hanging or crashing the tab is a far worse outcome than a
 * mis-drawn tree, so the guard is on both traversals.
 */
function countVisibleRows<T extends ExpandableTreeNode>(
  nodes: T[], expanded: Set<T>, visited: Set<T> = new Set<T>()): number {
  let rows = 0;
  for (const node of nodes || []) {
    if (!node || visited.has(node)) {
      continue;
    }
    visited.add(node);
    rows++;
    if (expanded.has(node) && node.children) {
      rows += countVisibleRows(node.children as T[], expanded, visited);
    }
  }
  return rows;
}

export function planTreeExpansion<T extends ExpandableTreeNode>(
  roots: T[],
  options: TreeExpansionOptions = {}): TreeExpansionPlan<T> {

  const rowBudget = ( typeof options.rowBudget === 'number' && options.rowBudget > 0 )
    ? options.rowBudget
    : DEFAULT_ROW_BUDGET;

  // --- one breadth-first pass ------------------------------------------------
  // `seen` is not defensive paranoia about duplicates so much as a stop for a
  // cycle: `ProjectFolders.moveFolder` used to be able to build one, and a
  // planner that hangs the browser is a worse outcome than a mis-drawn tree.
  const nodesByDepth: T[][] = [];
  const byPath = new Map<string, T>();
  const seen = new Set<T>();
  let totalNodes = 0;

  let frontier: T[] = Array.isArray(roots) ? roots.filter(node => !!node) : [];
  while (frontier.length > 0) {
    const level: T[] = [];
    const next: T[] = [];
    for (const node of frontier) {
      if (seen.has(node)) {
        continue;
      }
      seen.add(node);
      level.push(node);
      const key = Util.normalizeFolderPath(node.path);
      // First wins: two folders cannot share a path, but a malformed tree
      // should not let the second one steal the reveal.
      if (!byPath.has(key)) {
        byPath.set(key, node);
      }
      if (node.children) {
        for (const child of node.children as T[]) {
          if (child && !seen.has(child)) {
            next.push(child);
          }
        }
      }
    }
    if (level.length === 0) {
      break;
    }
    nodesByDepth.push(level);
    totalNodes += level.length;
    frontier = next;
  }

  if (nodesByDepth.length === 0) {
    return { expand: [], totalNodes: 0, visibleRows: 0, depth: -1, wholeTree: true };
  }

  // --- how deep does the budget reach? --------------------------------------
  const counts = nodesByDepth.map(level => level.length);
  const deepest = counts.length - 1;
  let depth: number;

  if (totalNodes <= rowBudget) {
    // Rule 1: it all fits, so depth never enters into it.
    depth = deepest;
  } else {
    // Rule 3: level 0 is expanded unconditionally, so start from the rows that
    // costs (level 0 plus the level 1 it reveals) rather than from zero.
    depth = 0;
    let rows = counts[0] + ( counts[1] || 0 );
    for (let level = 1; level < deepest; level++) {
      const rowsIfExpanded = rows + counts[level + 1];
      if (rowsIfExpanded > rowBudget) {
        break;
      }
      rows = rowsIfExpanded;
      depth = level;
    }
  }

  if ( typeof options.maxDepth === 'number' && options.maxDepth >= 0 && depth > options.maxDepth ) {
    depth = options.maxDepth;
  }

  // --- collect -------------------------------------------------------------
  const expand: T[] = [];
  const chosen = new Set<T>();
  const take = (node: T) => {
    if (node && hasChildren(node) && !chosen.has(node)) {
      chosen.add(node);
      expand.push(node);
    }
  };

  for (let level = 0; level <= depth && level < nodesByDepth.length; level++) {
    for (const node of nodesByDepth[level]) {
      take(node);
    }
  }

  // Rule 4. Root-first, so the chain is still parents-before-children.
  if (options.currentPath !== undefined && options.currentPath !== null) {
    for (const path of ancestorPaths(options.currentPath)) {
      const node = byPath.get(path);
      if (node) {
        take(node);
      }
    }
  }

  let expandableNodes = 0;
  for (const level of nodesByDepth) {
    for (const node of level) {
      if (hasChildren(node)) {
        expandableNodes++;
      }
    }
  }

  return {
    expand,
    totalNodes,
    visibleRows: countVisibleRows(nodesByDepth[0], chosen),
    depth,
    wholeTree: chosen.size === expandableNodes
  };
}


